import React, { useState, useCallback } from "react";
import { Bars } from "react-loader-spinner";
import { 
  FaWallet, 
  FaCheck, 
  FaUnlink, 
  FaEye, 
  FaEyeSlash, 
  FaStar, 
  FaInfoCircle,
  FaExclamationTriangle,
  FaPlus
} from "react-icons/fa";
import { HiLink } from "react-icons/hi";
import { useAccount, useSignMessage } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";

import notify from "../common/notify";
import {
  useGetLinkedWalletsQuery,
  useConnectWalletMutation,
  useDisconnectWalletMutation,
  useUpdateWalletVisibilityMutation,
  useSetWalletAsPrimaryMutation,
  useLazyGetWalletInfoQuery,
} from "../../redux/slices/api/userApi";

import { VERIFICATION_MESSAGE } from "../../Constants";
import WalletDisplay from "../common/WalletDisplay";

/**
 * Single wallet card component
 */
const WalletCard = ({
  wallet,
  isPrimary,
  onToggleVisibility,
  onUnlink,
  onSetPrimary,
  isUpdating,
}) => {
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const handleUnlinkClick = () => {
    if (!confirmUnlink) {
      setConfirmUnlink(true);
      return;
    }
    onUnlink(wallet.address);
    setConfirmUnlink(false);
  };

  return (
    <div className={`p-5 rounded-xl border-2 transition-all duration-300 backdrop-blur-sm ${
      isPrimary 
        ? "border-primary/30 bg-gradient-to-br from-primary/5 to-white ring-2 ring-primary/10" 
        : "border-gray-200 bg-white hover:border-primary/20 hover:shadow-md"
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div 
            className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isPrimary 
                ? "bg-gradient-to-br from-primary to-primary-green text-white shadow-md" 
                : "bg-gray-100 text-gray-500"
            }`}
          >
            <FaWallet className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <WalletDisplay
                address={wallet.address}
                className="font-mono font-semibold text-gray-800"
              />
              {isPrimary && (
                <span className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2.5 py-1 rounded-full border border-primary/20">
                  <FaStar className="w-3 h-3" />
                  Primary
                </span>
              )}
              {wallet.hasBlockchainCreations && (
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                  <FaInfoCircle className="w-3 h-3" />
                  Has creations
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {wallet.displayProfile 
                ? "Profile visible for this wallet's activities" 
                : "Profile hidden for this wallet's activities"}
            </p>
            {wallet.linkedAt && (
              <p className="text-xs text-gray-400 mt-1">
                Linked {new Date(wallet.linkedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Set as Primary Button */}
          {!isPrimary && (
            <button
              type="button"
              onClick={() => onSetPrimary(wallet.address)}
              disabled={isUpdating}
              className={`p-2.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-primary/10 hover:text-primary transition-all ${
                isUpdating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Set as primary wallet"
            >
              <FaStar className="w-4 h-4" />
            </button>
          )}
          
          {/* Visibility Toggle */}
          <button
            type="button"
            onClick={() => onToggleVisibility(wallet.address, !wallet.displayProfile)}
            disabled={isUpdating || (wallet.hasBlockchainCreations && wallet.displayProfile)}
            className={`p-2.5 rounded-lg transition-all ${
              wallet.displayProfile 
                ? "bg-primary/10 text-primary hover:bg-primary/20" 
                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
            } ${(isUpdating || (wallet.hasBlockchainCreations && wallet.displayProfile)) ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={
              wallet.hasBlockchainCreations && wallet.displayProfile
                ? "Cannot hide profile for wallets with creations"
                : wallet.displayProfile 
                  ? "Profile visible (click to hide)" 
                  : "Profile hidden (click to show)"
            }
          >
            {wallet.displayProfile ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
          </button>
          
          {/* Unlink Button */}
          <button
            type="button"
            onClick={handleUnlinkClick}
            disabled={isUpdating}
            className={`p-2.5 rounded-lg transition-all ${
              confirmUnlink 
                ? "bg-red-500 text-white" 
                : "bg-red-50 text-red-500 hover:bg-red-100"
            } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={confirmUnlink ? "Click again to confirm" : "Unlink wallet"}
          >
            <FaUnlink className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Unlink confirmation inline */}
      {confirmUnlink && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <FaExclamationTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">
                {wallet.hasBlockchainCreations 
                  ? "This wallet has created projects or communities. Your profile will still be associated with those creations."
                  : "Unlink this wallet from your profile?"}
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={handleUnlinkClick}
                  disabled={isUpdating}
                  className="px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-all disabled:opacity-70"
                >
                  {isUpdating ? "Unlinking..." : "Confirm Unlink"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmUnlink(false)}
                  className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LinkedWalletsSection Component
 * 
 * Displays and manages linked wallets in user profile settings.
 * Users can:
 * - Link new wallets
 * - Unlink existing wallets (with restrictions for creators)
 * - Toggle profile visibility for wallet activities
 * - Set primary wallet
 */
const LinkedWalletsSection = ({ isEdit = false }) => {
  const [isLinking, setIsLinking] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  
  // Wagmi hooks for wallet connection
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { openConnectModal } = useConnectModal();
  
  // API hooks
  const { data: linkedWalletsData, isLoading, refetch } = useGetLinkedWalletsQuery(undefined, {
    skip: !isEdit,
  });
  
  const [connectWallet, { isLoading: isConnecting }] = useConnectWalletMutation();
  const [disconnectWallet, { isLoading: isDisconnecting }] = useDisconnectWalletMutation();
  const [updateVisibility, { isLoading: isUpdatingVisibility }] = useUpdateWalletVisibilityMutation();
  const [setPrimary, { isLoading: isSettingPrimary }] = useSetWalletAsPrimaryMutation();
  const [getWalletInfo] = useLazyGetWalletInfoQuery();

  const isUpdating = isConnecting || isDisconnecting || isUpdatingVisibility || isSettingPrimary;
  
  const wallets = linkedWalletsData?.wallets || [];
  const primaryWallet = linkedWalletsData?.primaryWallet;

  /**
   * Check if a wallet is already linked
   */
  const isWalletLinked = useCallback((addr) => {
    if (!addr) return false;
    return wallets.some(w => w.address.toLowerCase() === addr.toLowerCase());
  }, [wallets]);

  /**
   * Handle linking a new wallet
   */
  const handleLinkWallet = async () => {
    if (!isConnected || !address) {
      // Open wallet connect modal
      openConnectModal?.();
      return;
    }

    // Check if already linked
    if (isWalletLinked(address)) {
      notify("This wallet is already linked to your account", "info");
      return;
    }

    setIsLinking(true);

    try {
      // First, check wallet info
      const infoResult = await getWalletInfo(address);
      
      if (infoResult.data?.canLink === false) {
        notify(infoResult.data.message || "Cannot link this wallet", "error");
        setIsLinking(false);
        return;
      }

      setWalletInfo(infoResult.data);

      // Sign message to prove ownership
      const signature = await signMessageAsync({
        message: VERIFICATION_MESSAGE,
      });

      // Send to backend
      const result = await connectWallet({
        body: {
          public_address: address,
          signature,
        },
      }).unwrap();

      // Update token if returned
      if (result.token) {
        localStorage.setItem("token", result.token);
      }

      notify("Wallet linked successfully!", "success");
      refetch();
    } catch (error) {
      console.error("Failed to link wallet:", error);
      if (error?.data?.message) {
        notify(error.data.message, "error");
      } else if (error?.message?.includes("User rejected")) {
        notify("Signature request was rejected", "info");
      } else {
        notify("Failed to link wallet. Please try again.", "error");
      }
    } finally {
      setIsLinking(false);
      setWalletInfo(null);
    }
  };

  /**
   * Handle unlinking a wallet
   */
  const handleUnlinkWallet = async (walletAddress) => {
    try {
      const result = await disconnectWallet({ address: walletAddress }).unwrap();
      
      // Update token if returned
      if (result.token) {
        localStorage.setItem("token", result.token);
      }
      
      notify(result.message || "Wallet unlinked successfully", "success");
      refetch();
    } catch (error) {
      notify(error?.data?.message || "Failed to unlink wallet", "error");
    }
  };

  /**
   * Handle visibility toggle
   */
  const handleToggleVisibility = async (walletAddress, displayProfile) => {
    try {
      await updateVisibility({ address: walletAddress, displayProfile }).unwrap();
      notify(
        displayProfile 
          ? "Profile will now be shown for this wallet's activities" 
          : "Profile hidden for this wallet's activities",
        "success"
      );
      refetch();
    } catch (error) {
      notify(error?.data?.message || "Failed to update visibility", "error");
    }
  };

  /**
   * Handle setting primary wallet
   */
  const handleSetPrimary = async (walletAddress) => {
    try {
      const result = await setPrimary({ address: walletAddress }).unwrap();
      
      // Update token if returned
      if (result.token) {
        localStorage.setItem("token", result.token);
      }
      
      notify("Primary wallet updated", "success");
      refetch();
    } catch (error) {
      notify(error?.data?.message || "Failed to set primary wallet", "error");
    }
  };

  // Show placeholder when not in edit mode
  if (!isEdit) {
    return (
      <div className="p-5 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
        <div className="text-center py-4">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <FaWallet className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm mb-2">
            Wallet linking will be available after you save your profile.
          </p>
          <p className="text-gray-400 text-xs">
            Link your wallets to associate your on-chain activities with your profile.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Bars
          height="30"
          width="30"
          color="#8538ff"
          ariaLabel="bars-loading"
          visible={true}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Info Banner */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-3">
          <FaInfoCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-700 font-medium">
              Link your wallets to display your profile for community memberships and project fundings.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              If you've previously funded projects or joined communities anonymously with a wallet, linking it will reveal your profile for those activities.
            </p>
          </div>
        </div>
      </div>

      {/* Wallets List */}
      <div className="space-y-4">
        {wallets.length === 0 ? (
          <div className="p-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-center">
            <FaWallet className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">No wallets linked yet</p>
            <p className="text-gray-400 text-xs mt-1">Link a wallet to get started</p>
          </div>
        ) : (
          wallets.map((wallet) => (
            <WalletCard
              key={wallet.address}
              wallet={wallet}
              isPrimary={wallet.isPrimary || wallet.address === primaryWallet}
              onToggleVisibility={handleToggleVisibility}
              onUnlink={handleUnlinkWallet}
              onSetPrimary={handleSetPrimary}
              isUpdating={isUpdating}
            />
          ))
        )}
      </div>

      {/* Link New Wallet Button */}
      <div className="mt-4">
        <button
          type="button"
          onClick={handleLinkWallet}
          disabled={isLinking || isUpdating}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed transition-all ${
            isConnected && !isWalletLinked(address)
              ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/50"
              : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:border-gray-300"
          } ${(isLinking || isUpdating) ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isLinking ? (
            <>
              <Bars height="16" width="16" color="#8538ff" ariaLabel="bars-loading" visible={true} />
              <span>Linking wallet...</span>
            </>
          ) : isConnected ? (
            <>
              {isWalletLinked(address) ? (
                <>
                  <FaCheck className="w-4 h-4 text-green-500" />
                  <span className="text-green-600">Connected wallet already linked</span>
                </>
              ) : (
                <>
                  <HiLink className="w-5 h-5" />
                  <span className="inline-flex min-w-0 items-center gap-1">
                    Link Connected Wallet (
                    <WalletDisplay address={address} showEnsBadge={false} />
                    )
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <FaPlus className="w-4 h-4" />
              <span>Connect & Link Wallet</span>
            </>
          )}
        </button>
      </div>

      {/* Wallet Info Modal/Notice */}
      {walletInfo && walletInfo.hasActivities && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-2">
            <FaExclamationTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-700 font-medium">
                This wallet has existing activities
              </p>
              <ul className="text-xs text-amber-600 mt-1 space-y-1">
                {walletInfo.hasCommunityMemberships && (
                  <li>• Community memberships will be associated with your profile</li>
                )}
                {walletInfo.hasFundings && (
                  <li>• Project fundings will be associated with your profile</li>
                )}
                {walletInfo.hasBlockchainCreations && (
                  <li>• Created projects/communities (identity cannot be hidden)</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedWalletsSection;
